// ============================================================
// OVERWATCH - a kill zone, declared and watched.
//
// You hold your fire and cover an arc. When somebody walks into it the
// app SAYS SO and stops there: no interrupt, no prompt, no stolen turn.
// The trigger conditions ("anything that isn't wearing our colours")
// are spoken at the table and never stored - a rule the app cannot
// evaluate is a rule it should not pretend to hold.
//
// The zone lives as a condition code on the watcher, `Ov:<heading>:<mode>`,
// so it is synced, undoable, chip-visible and expires with the other
// stances at the start of their next turn. `condType` collapses it to
// 'Ov', which is what makes it unique per combatant: you cover one arc.
//
// Pure. Zero React dependency.
// ============================================================

/** The arc a watcher covers, in degrees. Matches Suppressing Fire's. */
export const OVERWATCH_CONE_DEGREES = 45;

export interface OverwatchZone {
  /** Bearing in degrees, as the battlefield measures them. */
  heading: number;
  /** The fire mode held ready - 'single', 'semi', 'full'. */
  mode: string;
}

/**
 * `Ov:<heading>:<mode>`. The heading is rounded to whole degrees: it is
 * drawn as a 45-degree wedge, and a stored fraction would be a
 * precision the gesture never had.
 */
export function encodeOverwatch(zone: OverwatchZone): string {
  const heading = ((Math.round(zone.heading) % 360) + 360) % 360;
  return `Ov:${heading}:${zone.mode || 'single'}`;
}

/**
 * The zone a code carries, or null.
 *
 * A BARE `Ov` parses to null deliberately. The condition picker offers
 * one - the catalog has an entry for every name - and a GM marking
 * somebody as watching without pointing them anywhere is a real thing
 * to want. It draws no wedge, which is the honest rendering of a zone
 * with no bearing.
 */
export function parseOverwatch(code: string | null | undefined): OverwatchZone | null {
  if (!code) return null;
  const m = /^Ov:(-?\d+):(\w+)$/.exec(code);
  if (!m) return null;
  return { heading: parseInt(m[1], 10), mode: m[2] };
}

/** The zone this combatant is covering, if any. */
export function overwatchOf(conditions: readonly string[] | null | undefined): OverwatchZone | null {
  if (!conditions) return null;
  for (const c of conditions) {
    const zone = parseOverwatch(c);
    if (zone) return zone;
  }
  return null;
}

/**
 * A stable key for "this watcher has already been told about this
 * entrant, this round".
 *
 * The announcement fires on the MOVER's client, once per pair per round,
 * so a man pacing in and out of the same arc is announced once. The
 * round is part of the key because next round he is news again.
 */
export function announceKey(watcherId: string, entrantId: string, round: number): string {
  return `${watcherId}|${entrantId}|${round}`;
}
